/**
 * Renters Insurance Quote Wizard — H2
 * Updates:
 * - Final section is now NOTES ONLY (no preferred contact)
 * - Language toggle fixed-width wrapper prevents layout shift
 * - All styling colors centralized in RENTERS_THEME (externalized)
 */

import React, { useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, ProgressBar, Row } from "react-bootstrap";
import { RENTERS_COPY, IRentersCopyUnit } from "./Renters-Insurance_v02-strings";
import { RENTERS_THEME } from "./Renters-Insurance_v02-theme";

/* ============================== CONFIG ============================== */
const VERSION = "Renters-H2";
const SHOW_VERSION = false;

const TEST_MODE = false;
const API_GATEWAY_URL = "https://wi8zkeven2.execute-api.us-east-1.amazonaws.com/prod/feedback";

const DEMO_MODE_DEFAULT = true;
const DEMO_MODE_UI_ENABLED = true;

type ViewMode = "wizard" | "vertical";
type FormShape = Record<string, unknown>;
type SetForm = (patch: FormShape) => void;

export interface IWizardSection {
  id: string;
  title: React.ReactNode;
  explanation: React.ReactNode;
  enabled?: boolean;
  render: (form: FormShape, setForm: SetForm) => React.ReactNode;
}

const clampPct = (p: number) => Math.min(99, Math.max(0, Math.round(p)));

/* ============================== DATA ============================== */
const US_STATES: { value: string; label: string }[] = [
  { value: "AL", label: "Alabama" }, { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" }, { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" }, { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" }, { value: "DE", label: "Delaware" },
  { value: "DC", label: "District of Columbia" }, { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" }, { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" }, { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" }, { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" }, { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" }, { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" }, { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" }, { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" }, { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" }, { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" }, { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" }, { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" }, { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" }, { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" }, { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" }, { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" }, { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" }, { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" }, { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" }, { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" }, { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

/* ============================== HELPERS ============================== */
const S = (v: unknown): string | null => {
  if (v == null) return null;
  if (typeof v === "string") {
    const t = v.trim();
    return t === "" ? null : t;
  }
  return String(v);
};
const hasVal = (v: unknown): boolean => (typeof v === "string" ? v.trim().length > 0 : v != null);

/* ============================== COMPLETION SCHEMA ============================== */
type SectionKeys = {
  id: string;
  required: (form: FormShape) => string[];
  optional?: (form: FormShape) => string[];
};

const completionSchema: SectionKeys[] = [
  { id: "personal", required: () => ["firstName", "lastName", "dob", "email", "phone"] },
  { id: "address",  required: () => ["address1", "city", "state", "zip", "moveInDate"] },
  { id: "coverage", required: () => ["personalProperty", "liabilityLimit", "deductible", "waterBackup", "petLiability"] },
  {
    id: "roommates",
    required: (form) => {
      const has = ((form.hasRoommates as string) ?? "no") === "yes";
      return has ? ["roommateCount", "splitPolicy"] : [];
    },
  },
  { id: "discounts", required: () => [], optional: () => ["hasSprinklers", "hasAlarm", "bundleAuto"] },
  // Notes only — no required fields, completion counts if note has any content
  { id: "notes-only", required: () => [], optional: () => ["notes"] },
];

const requiredKeysForSection = (sectionId: string, form: FormShape): string[] =>
  completionSchema.find((s) => s.id === sectionId)?.required(form) ?? [];

const optionalKeysForSection = (sectionId: string, form: FormShape): string[] =>
  completionSchema.find((s) => s.id === sectionId)?.optional?.(form) ?? [];

const sectionIsComplete = (sectionId: string, form: FormShape): boolean => {
  const req = requiredKeysForSection(sectionId, form);
  if (req.length > 0) return req.every((k) => hasVal(form[k]));
  const opt = optionalKeysForSection(sectionId, form);
  return opt.length > 0 && opt.some((k) => hasVal(form[k]));
};

const overallCompletion = (enabledSectionIds: string[], form: FormShape): number => {
  const allReq: string[] = enabledSectionIds.flatMap((id) => requiredKeysForSection(id, form));
  const uniq = Array.from(new Set(allReq));
  if (uniq.length === 0) return 0;
  const filled = uniq.reduce((acc, k) => (hasVal(form[k]) ? acc + 1 : acc), 0);
  return (filled / uniq.length) * 100;
};

/* ============================== CONTROLS ============================== */
const WizardControls: React.FC<{
  onPrev: () => void;
  onNext: () => void;
  onSubmit: () => void;
  isFirst: boolean;
  isLast: boolean;
  submitting?: boolean;
  copy: IRentersCopyUnit;
}> = ({ onPrev, onNext, onSubmit, isFirst, isLast, submitting, copy }) => (
  <div className="d-flex justify-content-between">
    <Button variant="secondary" onClick={onPrev} disabled={isFirst || !!submitting}>
      {copy.buttons.previous}
    </Button>
    {isLast ? (
      <Button variant="success" onClick={onSubmit} disabled={!!submitting}>
        {submitting ? "Processing..." : copy.buttons.submit}
      </Button>
    ) : (
      <Button variant="primary" onClick={onNext} disabled={!!submitting}>
        {copy.buttons.next}
      </Button>
    )}
  </div>
);

/* ============================== WIZARD CORE ============================== */
export const DualViewWizard_Renters_H2: React.FC<{
  mode?: ViewMode;
  sections: IWizardSection[];
  initialForm?: FormShape;
  className?: string;
  onSubmit?: (form: FormShape) => void;
  submitting?: boolean;
  submitted?: boolean;
  copy: IRentersCopyUnit;
}> = ({
  mode = "wizard",
  sections,
  initialForm = {},
  className,
  onSubmit,
  submitting = false,
  submitted = false,
  copy,
}) => {
  const theme = RENTERS_THEME;
  const enabledSections = useMemo(
    () => sections.filter((s) => s.enabled !== false),
    [sections]
  );

  const [form, setFormState] = useState<FormShape>(initialForm);
  const setForm: SetForm = (patch) => setFormState((prev) => ({ ...prev, ...patch }));

  const [current, setCurrent] = useState(0);
  const [activeId, setActiveId] = useState<string>(enabledSections[0]?.id ?? "");

  const ids = useMemo(() => enabledSections.map((s) => s.id), [enabledSections]);
  const activeIndex = Math.max(0, ids.indexOf(activeId));
  const total = Math.max(1, enabledSections.length);
  const enabledIds = enabledSections.map((s) => s.id);

  const rawPct = overallCompletion(enabledIds, form);
  const pctLabel = submitted ? 100 : clampPct(rawPct);
  const pct = submitted ? 100 : clampPct(rawPct);

  // Section success visuals: Roommates only when toggle ON and fields complete
  const sectionShowsSuccess = (sectionId: string): boolean => {
    const isComplete = sectionIsComplete(sectionId, form);
    if (sectionId === "roommates") {
      const has = ((form.hasRoommates as string) ?? "no") === "yes";
      return has && isComplete;
    }
    return isComplete;
  };

  if (mode === "vertical") {
    return (
      <div className={className}>
        <Card
          className="position-sticky mb-3"
          style={{ top: RENTERS_THEME.layout.stickyTopPx, zIndex: 1, backgroundColor: theme.colors.stickyHeaderBg }}
        >
          <Card.Header>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <strong>
                  {copy.appTitle} {SHOW_VERSION ? `— ${VERSION}` : ""} · Section {activeIndex + 1} of {total}
                </strong>{" "}
                — {enabledSections[activeIndex]?.title}
              </div>
              <div style={{ minWidth: RENTERS_THEME.layout.progressMinWidthVerticalPx }}>
                <ProgressBar now={pct} label={`${pctLabel}%`} />
              </div>
            </div>
          </Card.Header>
        </Card>

        {enabledSections.map((s) => {
          const isActive = activeId === s.id;
          const successVisual = sectionShowsSuccess(s.id);

          const cardStyle: React.CSSProperties = {
            borderColor: successVisual ? theme.colors.borderSuccess : isActive ? theme.colors.borderPrimary : theme.colors.cardBorder,
            backgroundColor: successVisual ? theme.colors.successSubtleBg : theme.colors.backgroundColor,
          };

          const headerStyle: React.CSSProperties = isActive
            ? { backgroundColor: theme.colors.primary, color: theme.colors.onPrimary }
            : {};

          return (
            <Row key={s.id} className="mb-3" style={{ scrollMarginTop: RENTERS_THEME.layout.verticalScrollMarginTopPx }}>
              <Col
                md={8}
                onClick={() => setActiveId(s.id)}
                onFocusCapture={() => setActiveId(s.id)}
              >
                <Card style={cardStyle} className="border">
                  <Card.Header style={headerStyle}>
                    {s.title}
                    {successVisual && !isActive && (
                      <Badge bg="success" className="ms-2">{copy.buttons.completeBadge}</Badge>
                    )}
                    {successVisual && isActive && (
                      <Badge bg="light" text="dark" className="ms-2">{copy.buttons.completeBadge}</Badge>
                    )}
                  </Card.Header>
                  <Card.Body>{s.render(form, setForm)}</Card.Body>
                </Card>
              </Col>

              <Col md={4}>
                <Card className="border" style={{ borderColor: isActive ? theme.colors.borderPrimary : theme.colors.cardBorder }}>
                  <Card.Header style={isActive ? { backgroundColor: theme.colors.primary, color: theme.colors.onPrimary } : { backgroundColor: theme.colors.helpHeaderBg, color: theme.colors.helpHeaderText }}>
                    {copy.sectionHelpHeader}
                  </Card.Header>
                  <Card.Body>
                    <strong>{s.title}</strong>
                    <p className="mb-0">{s.explanation}</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          );
        })}

        <div className="d-flex justify-content-end mt-3">
          <Button variant="success" onClick={() => onSubmit?.(form)} disabled={!!submitting}>
            {submitting ? "Processing..." : copy.buttons.submit}
          </Button>
        </div>
      </div>
    );
  }

  const step = enabledSections[current];
  const successVisualForStep = sectionShowsSuccess(step.id);
  const cardStyleWizard: React.CSSProperties = successVisualForStep
    ? { borderColor: RENTERS_THEME.colors.borderSuccess, backgroundColor: RENTERS_THEME.colors.successSubtleBg }
    : {};

  return (
    <Card className={className ?? ""} style={cardStyleWizard}>
      <Card.Header>
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <strong>
              {copy.appTitle} {SHOW_VERSION ? `— ${VERSION}` : ""} · Step {current + 1} of {total}
            </strong>{" "}
            — {step.title}
          </div>
          <div style={{ minWidth: RENTERS_THEME.layout.progressMinWidthPx }}>
            <ProgressBar now={pct} label={`${pctLabel}%`} />
          </div>
        </div>
      </Card.Header>

      <Card.Body>
        {step.render(form, setForm)}

        <Card className="mt-3">
          <Card.Header>{copy.sectionHelpHeader}</Card.Header>
          <Card.Body>
            <strong>{step.title}</strong>
            <p className="mb-0">{step.explanation}</p>
          </Card.Body>
        </Card>

        <hr />
        <WizardControls
          onPrev={() => setCurrent((c) => Math.max(0, c - 1))}
          onNext={() => setCurrent((c) => Math.min(total - 1, c + 1))}
          onSubmit={() => onSubmit?.(form)}
          isFirst={current === 0}
          isLast={current === total - 1}
          submitting={submitting}
          copy={copy}
        />
      </Card.Body>
    </Card>
  );
};

/* ============================== APP SHELL (EXAMPLE USAGE) ============================== */
export const Renters_Insurance_v02: React.FC = () => {
  const [uiMode, setUiMode] = useState<ViewMode>("vertical");
  const [demoMode, setDemoMode] = useState<boolean>(DEMO_MODE_DEFAULT);

  // Language slider state: 0 = EN, 1 = ES
  const [langIndex, setLangIndex] = useState<number>(0);

  // Build current copy object from arrays; clamp to length-1
  const currentCopy: IRentersCopyUnit = useMemo(() => {
    const enArr = RENTERS_COPY.en;
    const esArr = RENTERS_COPY.es;
    const idxEN = Math.min(langIndex, Math.max(0, enArr.length - 1));
    const idxES = Math.min(langIndex, Math.max(0, esArr.length - 1));
    return langIndex === 0 ? enArr[idxEN] : esArr[idxES];
  }, [langIndex]);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const initialBase: FormShape = { hasRoommates: "no" };

  const DEMO_DATA: FormShape = {
    ...initialBase,
    // Personal
    firstName: "Jane",
    lastName: "Doe",
    dob: "1996-04-10",
    email: "jane.doe@example.com",
    phone: "(555) 555-1234",
    // Address
    address1: "123 Main St",
    address2: "Apt 5B",
    city: "Boulder",
    state: "CO",
    zip: "80302",
    moveInDate: "2024-08-01",
    // Coverage
    personalProperty: "30000",
    liabilityLimit: "_300k",
    deductible: "500",
    waterBackup: "yes",
    petLiability: "no",
    // Roommates (toggle off by default)
    hasRoommates: "no",
    roommateCount: "2",
    splitPolicy: "no",
    // Discounts
    hasSprinklers: "yes",
    hasAlarm: "yes",
    bundleAuto: "no",
    // Notes only
    notes: "Please contact after 5pm.",
  };

  const sections: IWizardSection[] = useMemo(() => {
    const languageStrings = currentCopy;

    const personal: IWizardSection = {
      id: "personal",
      title: languageStrings.personal.title,
      explanation: languageStrings.personal.help,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="firstName">
                <Form.Label>{languageStrings.personal.fields.firstName.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.firstName as string) ?? ""}
                  onChange={(e) => setForm({ firstName: e.target.value })}
                  placeholder={languageStrings.personal.fields.firstName.placeholder}
                  isValid={hasVal(form.firstName)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="lastName">
                <Form.Label>{languageStrings.personal.fields.lastName.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.lastName as string) ?? ""}
                  onChange={(e) => setForm({ lastName: e.target.value })}
                  placeholder={languageStrings.personal.fields.lastName.placeholder}
                  isValid={hasVal(form.lastName)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="dob">
                <Form.Label>{languageStrings.personal.fields.dob.label}</Form.Label>
                <Form.Control
                  type="date"
                  value={(form.dob as string) ?? ""}
                  onChange={(e) => setForm({ dob: e.target.value })}
                  isValid={hasVal(form.dob)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="email">
                <Form.Label>{languageStrings.personal.fields.email.label}</Form.Label>
                <Form.Control
                  type="email"
                  value={(form.email as string) ?? ""}
                  onChange={(e) => setForm({ email: e.target.value })}
                  placeholder={languageStrings.personal.fields.email.placeholder}
                  isValid={hasVal(form.email)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="phone">
                <Form.Label>{languageStrings.personal.fields.phone.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.phone as string) ?? ""}
                  onChange={(e) => setForm({ phone: e.target.value })}
                  placeholder={languageStrings.personal.fields.phone.placeholder}
                  inputMode="tel"
                  isValid={hasVal(form.phone)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    };

    const address: IWizardSection = {
      id: "address",
      title: languageStrings.address.title,
      explanation: languageStrings.address.help,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            <Col md={8}>
              <Form.Group controlId="address1">
                <Form.Label>{languageStrings.address.fields.address1.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.address1 as string) ?? ""}
                  onChange={(e) => setForm({ address1: e.target.value })}
                  placeholder={languageStrings.address.fields.address1.placeholder}
                  autoComplete="address-line1"
                  isValid={hasVal(form.address1)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="address2">
                <Form.Label>{languageStrings.address.fields.address2.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.address2 as string) ?? ""}
                  onChange={(e) => setForm({ address2: e.target.value })}
                  placeholder={languageStrings.address.fields.address2.placeholder}
                  autoComplete="address-line2"
                  isValid={hasVal(form.address2)}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group controlId="city">
                <Form.Label>{languageStrings.address.fields.city.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.city as string) ?? ""}
                  onChange={(e) => setForm({ city: e.target.value })}
                  placeholder={languageStrings.address.fields.city.placeholder}
                  isValid={hasVal(form.city)}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="state">
                <Form.Label>{languageStrings.address.fields.state.label}</Form.Label>
                <Form.Select
                  value={(form.state as string) ?? ""}
                  onChange={(e) => setForm({ state: e.target.value })}
                  isValid={hasVal(form.state)}
                >
                  <option value="">{languageStrings.address.fields.state.placeholder}</option>
                  {US_STATES.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="zip">
                <Form.Label>{languageStrings.address.fields.zip.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.zip as string) ?? ""}
                  onChange={(e) => setForm({ zip: e.target.value })}
                  placeholder={languageStrings.address.fields.zip.placeholder}
                  inputMode="numeric"
                  isValid={hasVal(form.zip)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="moveInDate">
                <Form.Label>{languageStrings.address.fields.moveInDate.label}</Form.Label>
                <Form.Control
                  type="date"
                  value={(form.moveInDate as string) ?? ""}
                  onChange={(e) => setForm({ moveInDate: e.target.value })}
                  isValid={hasVal(form.moveInDate)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    };

    const coverage: IWizardSection = {
      id: "coverage",
      title: languageStrings.coverage.title,
      explanation: languageStrings.coverage.help,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="personalProperty">
                <Form.Label>{languageStrings.coverage.fields.personalProperty.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.personalProperty as string) ?? ""}
                  onChange={(e) => setForm({ personalProperty: e.target.value })}
                  placeholder={languageStrings.coverage.fields.personalProperty.placeholder}
                  inputMode="numeric"
                  isValid={hasVal(form.personalProperty)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="liabilityLimit">
                <Form.Label>{languageStrings.coverage.fields.liabilityLimit.label}</Form.Label>
                <Form.Select
                  value={(form.liabilityLimit as string) ?? ""}
                  onChange={(e) => setForm({ liabilityLimit: e.target.value })}
                  isValid={hasVal(form.liabilityLimit)}
                >
                  <option value="">{languageStrings.coverage.fields.liabilityLimit.placeholder}</option>
                  <option value="_100k">{languageStrings.coverage.fields.liabilityLimit.options._100k}</option>
                  <option value="_300k">{languageStrings.coverage.fields.liabilityLimit.options._300k}</option>
                  <option value="_500k">{languageStrings.coverage.fields.liabilityLimit.options._500k}</option>
                  <option value="custom">{languageStrings.coverage.fields.liabilityLimit.options.custom}</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="deductible">
                <Form.Label>{languageStrings.coverage.fields.deductible.label}</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.deductible as string) ?? ""}
                  onChange={(e) => setForm({ deductible: e.target.value })}
                  placeholder={languageStrings.coverage.fields.deductible.placeholder}
                  inputMode="numeric"
                  isValid={hasVal(form.deductible)}
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group controlId="waterBackup">
                <Form.Label>{languageStrings.coverage.fields.waterBackup.label}</Form.Label>
                <Form.Select
                  value={(form.waterBackup as string) ?? ""}
                  onChange={(e) => setForm({ waterBackup: e.target.value })}
                  isValid={hasVal(form.waterBackup)}
                >
                  <option value="">{languageStrings.coverage.fields.waterBackup.placeholder}</option>
                  <option value="yes">{languageStrings.coverage.fields.waterBackup.options.yes}</option>
                  <option value="no">{languageStrings.coverage.fields.waterBackup.options.no}</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group controlId="petLiability">
                <Form.Label>{languageStrings.coverage.fields.petLiability.label}</Form.Label>
                <Form.Select
                  value={(form.petLiability as string) ?? ""}
                  onChange={(e) => setForm({ petLiability: e.target.value })}
                  isValid={hasVal(form.petLiability)}
                >
                  <option value="">{languageStrings.coverage.fields.petLiability.placeholder}</option>
                  <option value="yes">{languageStrings.coverage.fields.petLiability.options.yes}</option>
                  <option value="no">{languageStrings.coverage.fields.petLiability.options.no}</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    };

    const roommates: IWizardSection = {
      id: "roommates",
      title: languageStrings.roommates.title,
      explanation: languageStrings.roommates.help,
      enabled: true,
      render: (form, setForm) => {
        const hasRoommates = ((form.hasRoommates as string) ?? "no") === "yes";
        return (
          <Form>
            <Row className="g-3">
              <Col md={12}>
                <Form.Check
                  type="checkbox"
                  id="hasRoommates"
                  label={languageStrings.roommates.hasRoommatesCheckbox}
                  checked={hasRoommates}
                  onChange={(e) => setForm({ hasRoommates: e.target.checked ? "yes" : "no" })}
                />
              </Col>

              {hasRoommates && (
                <>
                  <Col md={6}>
                    <Form.Group controlId="roommateCount">
                      <Form.Label>{languageStrings.roommates.fields.roommateCount.label}</Form.Label>
                      <Form.Control
                        type="text"
                        value={(form.roommateCount as string) ?? ""}
                        onChange={(e) => setForm({ roommateCount: e.target.value })}
                        placeholder={languageStrings.roommates.fields.roommateCount.placeholder}
                        inputMode="numeric"
                        isValid={hasVal(form.roommateCount)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="splitPolicy">
                      <Form.Label>{languageStrings.roommates.fields.splitPolicy.label}</Form.Label>
                      <Form.Select
                        value={(form.splitPolicy as string) ?? ""}
                        onChange={(e) => setForm({ splitPolicy: e.target.value })}
                        isValid={hasVal(form.splitPolicy)}
                      >
                        <option value="">{languageStrings.roommates.fields.splitPolicy.placeholder}</option>
                        <option value="yes">{languageStrings.roommates.fields.splitPolicy.options.yes}</option>
                        <option value="no">{languageStrings.roommates.fields.splitPolicy.options.no}</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </>
              )}
            </Row>
          </Form>
        );
      },
    };

    // FINAL SECTION: NOTES ONLY
    const notesOnly: IWizardSection = {
      id: "notes-only",
      title: languageStrings.contactPrefs.title, // reuse title like “Contact Preferences / Notes”, or adjust copy file label to just “Notes”
      explanation: languageStrings.contactPrefs.help,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            <Col md={12}>
              <Form.Group controlId="notes">
                <Form.Label>{languageStrings.contactPrefs.fields.notes.label}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={(form.notes as string) ?? ""}
                  onChange={(e) => setForm({ notes: e.target.value })}
                  placeholder={languageStrings.contactPrefs.fields.notes.placeholder}
                  isValid={hasVal(form.notes)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    };

    const discounts: IWizardSection = {
      id: "discounts",
      title: languageStrings.discounts.title,
      explanation: languageStrings.discounts.help,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            <Col md={4}>
              <Form.Group controlId="hasSprinklers">
                <Form.Label>{languageStrings.discounts.fields.hasSprinklers.label}</Form.Label>
                <Form.Select
                  value={(form.hasSprinklers as string) ?? ""}
                  onChange={(e) => setForm({ hasSprinklers: e.target.value })}
                  isValid={hasVal(form.hasSprinklers)}
                >
                  <option value="">{languageStrings.discounts.fields.hasSprinklers.placeholder}</option>
                  <option value="yes">{languageStrings.discounts.fields.hasSprinklers.options.yes}</option>
                  <option value="no">{languageStrings.discounts.fields.hasSprinklers.options.no}</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="hasAlarm">
                <Form.Label>{languageStrings.discounts.fields.hasAlarm.label}</Form.Label>
                <Form.Select
                  value={(form.hasAlarm as string) ?? ""}
                  onChange={(e) => setForm({ hasAlarm: e.target.value })}
                  isValid={hasVal(form.hasAlarm)}
                >
                  <option value="">{languageStrings.discounts.fields.hasAlarm.placeholder}</option>
                  <option value="yes">{languageStrings.discounts.fields.hasAlarm.options.yes}</option>
                  <option value="no">{languageStrings.discounts.fields.hasAlarm.options.no}</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="bundleAuto">
                <Form.Label>{languageStrings.discounts.fields.bundleAuto.label}</Form.Label>
                <Form.Select
                  value={(form.bundleAuto as string) ?? ""}
                  onChange={(e) => setForm({ bundleAuto: e.target.value })}
                  isValid={hasVal(form.bundleAuto)}
                >
                  <option value="">{languageStrings.discounts.fields.bundleAuto.placeholder}</option>
                  <option value="yes">{languageStrings.discounts.fields.bundleAuto.options.yes}</option>
                  <option value="no">{languageStrings.discounts.fields.bundleAuto.options.no}</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    };

    return [personal, address, coverage, roommates, discounts, notesOnly];
  }, [currentCopy]);

  const initialForm = demoMode ? DEMO_DATA : initialBase;

  const handleSubmit = async (payload: FormShape) => {
    setSubmitError(null);
    setSubmitting(true);

    const enabledIds = sections.filter(s => s.enabled !== false).map(s => s.id);
    const raw = overallCompletion(enabledIds, payload);
    const pctToSend = clampPct(raw);

    const customer_DATA = {
      productMode: "renters",
      submittedAtIso: new Date().toISOString(),
      source: `Renters Quote Wizard — ${VERSION}`,

      // Personal
      firstName: S(payload.firstName),
      lastName: S(payload.lastName),
      dob: S(payload.dob),
      email: S(payload.email),
      phone: S(payload.phone),

      // Address
      address1: S(payload.address1),
      address2: S(payload.address2),
      city: S(payload.city),
      state: S(payload.state),
      zip: S(payload.zip),
      moveInDate: S(payload.moveInDate),

      // Coverage
      personalProperty: S(payload.personalProperty),
      liabilityLimit: S(payload.liabilityLimit),
      deductible: S(payload.deductible),
      waterBackup: S(payload.waterBackup),
      petLiability: S(payload.petLiability),

      // Roommates
      hasRoommates: S(payload.hasRoommates),
      roommateCount: S(payload.roommateCount),
      splitPolicy: S(payload.splitPolicy),

      // Discounts
      hasSprinklers: S(payload.hasSprinklers),
      hasAlarm: S(payload.hasAlarm),
      bundleAuto: S(payload.bundleAuto),

      // Notes (final section)
      notes: S(payload.notes),

      // Progress
      formCompletionPercent: String(pctToSend),

      // Meta
      domainName: String(window.location.hostname),
      itemCategory: String("Customer.renters.quote"),
      itemClass: String("quote.request"),
      itemType: String("legacy.renters"),
      clientVersion: String(VERSION + ".renters"),
    };

    if (TEST_MODE) {
      console.log("TEST MODE — customer_DATA:", customer_DATA);
      setSubmitting(false);
      return;
    }

    try {
      const resp = await fetch(API_GATEWAY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(customer_DATA),
      });
      if (!resp.ok) throw new Error("network");
      setSubmitted(true);
    } catch {
      setSubmitError("Error: network error, try again in a minute.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="container my-5">
        <Row className="justify-content-center">
          <Col md={8}>
            <Card>
              <Card.Header className="fw-bold">{currentCopy.submitThankYou.header}</Card.Header>
              <Card.Body>
                <p className="mb-0">{currentCopy.submitThankYou.body}</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="container my-4">
      {/* Header + Language toggle + Demo + View toggles */}
     

          {/* Header + Controls (Grid-based, no jiggle) */}
          <div className="container mb-3">
            <Row className="align-items-center g-2">
              {/* Left: Title (locks to this column) */}
              <Col xs={12} md={6}>
                <h3 className="m-0">
                  {currentCopy.appTitle} {SHOW_VERSION ? `— ${VERSION}` : ""}
                </h3>
              </Col>

              {/* Right: Controls (stays in this column) */}
              <Col xs={12} md={6}>
                <Row className="g-2 align-items-center justify-content-md-end">
                  {/* Language switch cluster */}
                  
                  
                  <Col xs="auto" md={5}>
                    <div className="d-flex align-items-center">


                      <span className="text-nowrap me-2">{currentCopy.buttons.langEN}</span>
                      <div
                        style={{
                          width: RENTERS_THEME.layout.langSwitchWidthPx, // fixed width to prevent wiggle
                          display: "inline-flex",
                          justifyContent: "center",
                        }}
                      >

                        <Form.Check
                          type="switch"
                          id="langToggle"
                          checked={langIndex === 1}
                          onChange={(e) => setLangIndex(e.target.checked ? 1 : 0)}
                        />
                      </div>
                      <span className="text-nowrap ms-2">{currentCopy.buttons.langES}</span>
                    </div>
                  </Col>

                  {/* Demo toggle (optional) */}
                  {DEMO_MODE_UI_ENABLED && (
                    <Col xs="auto" md={2}>
                      <Form.Check
                        type="checkbox"
                        id="demoModeToggle"
                        label={currentCopy.buttons.demoToggle}
                        checked={demoMode}
                        onChange={(e) => setDemoMode(e.target.checked)}
                        disabled={submitting}
                      />
                    </Col>
                  )}

                  {/* View buttons */}
                  <Col xs="auto" md={5}>
                    <div className="d-flex gap-2">
                      <Button
                        variant={uiMode === "wizard" ? "primary" : "outline-primary"}
                        onClick={() => setUiMode("wizard")}
                        disabled={submitting}
                      >
                        {currentCopy.buttons.wizardView}
                      </Button>
                      <Button
                        variant={uiMode === "vertical" ? "primary" : "outline-primary"}
                        onClick={() => setUiMode("vertical")}
                        disabled={submitting}
                      >
                        {currentCopy.buttons.verticalView}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>


























      {submitError && <Alert variant="danger" className="mb-3">{submitError}</Alert>}

      <DualViewWizard_Renters_H2
        key={`${demoMode ? "demo" : "blank"}-${langIndex}`}
        mode={uiMode}
        sections={sections}
        initialForm={demoMode ? DEMO_DATA : initialBase}
        onSubmit={handleSubmit}
        submitting={submitting}
        submitted={false}
        copy={currentCopy}
      />
    </div>
  );
};

export default Renters_Insurance_v02;
